import React from "react";
import "../Styles/Demo.scss";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  FileText,
  MessageSquare,
  BarChart2,
  CheckCircle2,
  Volume2,
} from "lucide-react";

// Helper to render high-fidelity custom visual graphic mockups when image is missing
const RenderCustomVisualStage = ({ demo }) => {
  const title = (demo.title || "").toLowerCase();

  if (title.includes("restaurant") || title.includes("order") || title.includes("whatsapp") || title.includes("bot")) {
    return (
      <div className="custom-graphic-stage whatsapp-stage">
        <div className="apple-spatial-stage-glow" />
        <div className="whatsapp-chat-card">
          <div className="chat-bubble user-bubble">
            <MessageSquare size={12} color="#F05742" />
            <span>Place Order #8492</span>
          </div>
          <div className="typing-indicator">
            <span className="dot d1" />
            <span className="dot d2" />
            <span className="dot d3" />
          </div>
          <div className="chat-bubble ai-bubble">
            <CheckCircle2 size={13} color="#10B981" />
            <span>AI Agent Confirmed</span>
          </div>
        </div>
      </div>
    );
  }

  if (
    title.includes("voice") ||
    title.includes("call") ||
    title.includes("audio") ||
    title.includes("reminder") ||
    title.includes("assistant") ||
    title.includes("avatar")
  ) {
    return (
      <div className="custom-graphic-stage voice-stage">
        <div className="apple-spatial-stage-glow" />
        <div className="voice-console-card">
          <div className="voice-header-bar">
            <span className="live-status-pill">
              <span className="live-pulse-dot" />
              LIVE VOICE AGENT
            </span>
            <span className="time-code">03:42</span>
          </div>
          <div className="voice-waveform-row">
            <Volume2 size={16} color="#F05742" />
            <div className="waveform-lines">
              <span className="bar b1" />
              <span className="bar b2" />
              <span className="bar b3" />
              <span className="bar b4" />
              <span className="bar b5" />
              <span className="bar b6" />
              <span className="bar b7" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (
    title.includes("insight") ||
    title.includes("data") ||
    title.includes("sheet") ||
    title.includes("excel") ||
    title.includes("analytics") ||
    title.includes("hospitality") ||
    title.includes("pos")
  ) {
    return (
      <div className="custom-graphic-stage analytics-stage">
        <div className="apple-spatial-stage-glow" />
        <div className="analytics-console-card">
          <div className="analytics-top-row">
            <div className="analytics-icon-badge">
              <BarChart2 size={16} color="#F05742" />
            </div>
            <span className="speed-pill">⚡ &lt; 4ms SQL</span>
          </div>
          <div className="analytics-val-row">
            <span className="val-currency">+$148,290.00</span>
            <span className="growth-badge">+24.8%</span>
          </div>
          <div className="sparkline-bar-row">
            <span className="spark-bar s1" />
            <span className="spark-bar s2" />
            <span className="spark-bar s3" />
            <span className="spark-bar s4" />
            <span className="spark-bar s5" />
            <span className="spark-bar s6" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="custom-graphic-stage doc-stage">
      <div className="apple-spatial-stage-glow" />
      <div className="doc-scanner-card">
        <div className="laser-beam" />
        <div className="doc-header-row">
          <FileText size={16} color="#F05742" />
          <span className="doc-title-text">{demo.title}</span>
        </div>
        <div className="doc-badge-row">
          <span className="precision-pill">99.8% Precision</span>
          <span className="json-pill">JSON Output</span>
        </div>
      </div>
    </div>
  );
};

// Framer Motion Stagger Variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.05,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 28, scale: 0.96 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.4,
      ease: [0.16, 1, 0.3, 1],
    },
  },
};

const Demo = ({ content = [], productTitle = "DocsAI" }) => {
  const navigate = useNavigate();

  if (!content.length) return null;

  return (
    <div className="demo-wrapper">
      <section className="demo-section">
        {/* Apple Editorial Tag & Headline */}
        <div className="demo-header-meta">
          <span className="demo-label-apple">DEMOS</span>
          <h2 className="demo-main-title">Interactive AI Experiences</h2>
          <p className="demo-main-sub">
            Experience {productTitle} with real business workflows.
          </p>
        </div>

        {/* 4-Column Apple Spatial Grid with Staggered Entrance Animation */}
        <div className="demo-container">
          <motion.div
            className="demo-grid"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {content.map((demo, index) => (
              <motion.div
                className="demo-card-v2"
                key={demo.title + index}
                variants={cardVariants}
                onClick={() => {
                  if (demo.url) {
                    window.open(demo.url, "_blank");
                  } else if (demo.path) {
                    navigate(demo.path);
                  }
                }}
              >
                {/* 3D / High-Key Visual Stage (~200px Height Container) */}
                <div className="demo-card-icon-container">
                  <div
                    className="demo-fallback-wrapper"
                    style={{
                      display: "flex",
                      width: "100%",
                      height: "100%",
                    }}
                  >
                    <RenderCustomVisualStage demo={demo} />
                  </div>
                </div>

                {/* Editorial Content Body */}
                <div className="demo-card-body">
                  <h3 className="demo-card-title">{demo.title}</h3>
                  {demo.description?.length ? (
                    <p className="demo-description">{demo.description[0]}</p>
                  ) : (
                    <p className="demo-description">
                      Explore automated AI agent workflows built for modern teams.
                    </p>
                  )}
                </div>

                {/* Coral Link Footer: Try demo → */}
                <div className="demo-card-footer">
                  <span className="demo-link-btn">
                    <span>Explore Demo</span>
                    <ArrowRight size={15} className="demo-link-arrow" />
                  </span>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Demo;
